import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Search, Clock, ArrowRight, BookOpen, Tag, ChevronLeft, ChevronRight,
  FileText, Lightbulb, Cpu, Megaphone
} from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import ShareButtons from '@/components/ShareButtons';
import { SocialImage, blogCoverSrc } from '@/components/SocialImage';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  regulatory: FileText,
  'thought-leadership': Lightbulb,
  technical: Cpu,
  product: Megaphone,
};

const CATEGORY_META = {
  regulatory: { label: 'Regulatory Updates', color: '#0066CC' },
  'thought-leadership': { label: 'Thought Leadership', color: '#14b8a6' },
  technical: { label: 'Technical', color: '#8b5cf6' },
  product: { label: 'Product Updates', color: '#f59e0b' },
};

const BlogPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (category) params.set('category', category);
      if (search) params.set('search', search);
      const res = await fetch(`${API}/api/blog/articles?${params}`);
      if (res.ok) {
        const data = await res.json();
        setArticles(data.articles || []);
        setTotalPages(data.pages || 1);
      }
    } catch {}
    finally { setLoading(false); }
  }, [category, search, page]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleCategoryClick = (cat) => {
    setCategory(prev => prev === cat ? null : cat);
    setPage(1);
  };

  // Featured = most recent article (not hardcoded to featured flag)
  const featured = articles.length > 0 ? articles[0] : null;
  const rest = articles.slice(category || search ? 0 : 1);

  return (
    <PageLayout>
      {/* Hero */}
      <section className="pb-12 pt-4" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 text-taxera-teal text-sm font-medium rounded-full mb-6">
              <BookOpen className="w-4 h-4" strokeWidth={1.5} />
              Resources
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-taxera-charcoal leading-tight mb-5">
              Insights for tax <br />
              <span className="taxera-gradient-text">technology leaders.</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl">
              Regulatory intelligence, strategic analysis, and technical deep-dives from the Taxera team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="pb-8" data-testid="blog-filters">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {Object.entries(CATEGORY_META).map(([key, meta]) => {
                const Icon = CATEGORY_ICONS[key];
                const isActive = category === key;
                return (
                  <button
                    key={key}
                    onClick={() => handleCategoryClick(key)}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold transition-all ${
                      isActive
                        ? 'text-white shadow-md'
                        : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                    style={isActive ? { backgroundColor: meta.color } : {}}
                    data-testid={`filter-${key}`}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    {meta.label}
                  </button>
                );
              })}
            </div>
            {/* Search */}
            <form onSubmit={handleSearch} className="relative w-full md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search articles..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal focus:ring-1 focus:ring-taxera-teal/20"
                data-testid="blog-search-input"
              />
            </form>
          </div>
          {(category || search) && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-slate-400">
                {articles.length} article{articles.length !== 1 ? 's' : ''} found
              </span>
              <button
                onClick={() => { setCategory(null); setSearch(''); setSearchInput(''); setPage(1); }}
                className="text-xs text-taxera-teal font-semibold hover:underline"
                data-testid="clear-filters"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Featured Article */}
      {featured && !category && !search && (
        <section className="pb-12" data-testid="featured-article">
          <div className="max-w-7xl mx-auto px-6">
            <FeaturedCard article={featured} />
          </div>
        </section>
      )}

      {/* Article Grid */}
      <section className="pb-20" data-testid="article-grid">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map(i => (
                <div key={i} className="bg-slate-50 rounded-2xl h-72 animate-pulse" />
              ))}
            </div>
          ) : rest.length === 0 ? (
            <div className="text-center py-16 bg-slate-50 rounded-3xl border border-slate-200" data-testid="no-articles">
              <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">No articles found</h3>
              <p className="text-sm text-slate-500">Try a different search or category filter.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((article, i) => (
                <ArticleCard key={article.slug} article={article} index={i} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:border-slate-300 disabled:opacity-30"
                data-testid="prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-slate-500 px-3">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg border border-slate-200 text-slate-400 hover:border-slate-300 disabled:opacity-30"
                data-testid="next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
};

/* ─── Featured Card ─── */
const FeaturedCard = ({ article }) => {
  const [coverMissing, setCoverMissing] = useState(false);
  const cover = blogCoverSrc(article);
  const showCover = cover && !coverMissing;
  const catMeta = CATEGORY_META[article.category];
  const CatIcon = CATEGORY_ICONS[article.category];

  return (
    <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}>
      <Link to={`/blog/${article.slug}`} className="group block" data-testid={`featured-${article.slug}`}>
        <div className="relative rounded-3xl overflow-hidden bg-slate-900">
          <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 30% 50%, ${article.cover_color || '#0066CC'}40, transparent 70%)` }} />
          <div className={`relative z-10 p-8 md:p-12 lg:p-16 ${showCover ? 'lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center' : ''}`}>
            <div>
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-2xs font-bold uppercase tracking-wide text-white" style={{ backgroundColor: catMeta?.color || '#666' }}>
                {CatIcon && <CatIcon className="w-3 h-3" />}
                {catMeta?.label}
              </span>
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {article.reading_time} min read
              </span>
              {article.created_at && (
                <span className="text-xs text-slate-500">
                  {new Date(article.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </span>
              )}
            </div>
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 group-hover:text-taxera-teal transition-colors leading-tight max-w-3xl">
              {article.title}
            </h2>
            <p className="text-base text-slate-400 mb-6 max-w-2xl leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: catMeta?.color || '#666' }}>
                  {article.author?.name?.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">{article.author?.name}</div>
                  <div className="text-2xs text-slate-500">{article.author?.role}</div>
                </div>
              </div>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-taxera-teal group-hover:gap-3 transition-all">
                Read article <ArrowRight className="w-4 h-4" />
              </span>
            </div>
            </div>
            {showCover && (
              <SocialImage
                src={cover}
                thumbs={false}
                eager
                alt={article.title}
                width={520}
                height={272}
                className="hidden lg:block w-full max-w-[520px] h-auto aspect-[1.91/1] object-cover rounded-[16px] justify-self-end transition-[transform,box-shadow] duration-500 ease-out group-hover:-translate-y-1 group-hover:scale-[1.02] group-hover:shadow-2xl group-hover:shadow-black/40"
                testId="featured-cover"
              linked
                onMissing={() => setCoverMissing(true)}
              />
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

/* ─── Article Card ─── */
const ArticleCard = ({ article, index }) => {
  const [coverMissing, setCoverMissing] = useState(false);
  const cover = blogCoverSrc(article);
  const catMeta = CATEGORY_META[article.category];
  const CatIcon = CATEGORY_ICONS[article.category];

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <Link to={`/blog/${article.slug}`} className="group block h-full" data-testid={`article-${article.slug}`}>
        <div className="h-full rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:border-slate-300 hover:-translate-y-1 transition-all duration-300">
          {cover && !coverMissing ? (
            <div className="overflow-hidden">
            <SocialImage
              src={cover}
              alt={article.title}
              width={600}
              height={314}
              className="block w-full h-auto aspect-[1.91/1] object-cover bg-slate-100 transition-transform duration-500 ease-out group-hover:scale-[1.04]"
              testId={`article-cover-${article.slug}`}
              linked
              onMissing={() => setCoverMissing(true)}
            />
            </div>
          ) : (
            <div className="h-1.5" style={{ backgroundColor: article.cover_color || '#0066CC' }} />
          )}
          <div className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-2xs font-bold uppercase tracking-wide" style={{ backgroundColor: `${catMeta?.color}12`, color: catMeta?.color }}>
                {CatIcon && <CatIcon className="w-3 h-3" />}
                {catMeta?.label}
              </span>
              <span className="text-2xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {article.reading_time} min
              </span>
            </div>
            {article.created_at && (
              <div className="text-2xs text-slate-400 mb-3">
                {new Date(article.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </div>
            )}
            <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-3 line-clamp-2 group-hover:text-taxera-blue transition-colors leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-slate-500 mb-4 line-clamp-3 leading-relaxed">
              {article.excerpt}
            </p>
            {/* Tags */}
            {article.tags && (
              <div className="flex flex-wrap gap-1 mb-4">
                {article.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="px-2 py-0.5 bg-slate-50 text-2xs text-slate-500 rounded-full border border-slate-100">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {/* Author */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full flex items-center justify-center text-2xs font-bold text-white" style={{ backgroundColor: catMeta?.color || '#666' }}>
                  {article.author?.name?.split(' ').map(n => n[0]).join('')}
                </div>
                <span className="text-xs text-slate-500">{article.author?.name}</span>
              </div>
              <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-taxera-blue group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </div>
      </Link>
      <div className="px-6 pb-4 -mt-2" onClick={(e) => e.stopPropagation()}>
        <ShareButtons url={`${window.location.origin}/blog/${article.slug}`} title={article.title} summary={article.excerpt} compact />
      </div>
    </motion.div>
  );
};

export default BlogPage;
